/**
 * 261104 E1-P2 Bước 3 — màn chặng viết: writing-running · writing-read · writing-paused · writing-error (+ nút phụ khi hết 3 thế hệ) · writing-boundary · writing-pushing.
 * Chỉ hiển thị; mọi hành động đi qua props; chữ/nút do `writingCopyOf` (util thuần, có test). Không import antd.
 */
import React, { useEffect, useState } from 'react';
import type { SkillBoundaryChoice, SkillEpisodeDetail, SkillRunProjection } from '../../../types/skillAdaptation';
import { Alert } from './SkillParts';
import {
  READER_NOTE, boundaryCardsOf, episodeCellsOf, readerChipsOf, readerNavOf, scriptBlocksOf, writingCopyOf,
} from './skillWritingUtils';

export interface WritingScreenProps {
  run: SkillRunProjection; stateId: string; busy: boolean; openEp: number | null;
  loadEpisode: (n: number) => Promise<SkillEpisodeDetail>;
  onOpenEp: (n: number | null) => void; onPause: () => void; onResume: () => void; onRaiseCap: () => void;
  onBoundary: (n: number, choice: SkillBoundaryChoice) => void; onStopEarly: () => void;
}

/** Khung đọc bản sạch một tập (không gọi AI). */
export const EpisodeReader: React.FC<{ run: SkillRunProjection; n: number; load: (n: number) => Promise<SkillEpisodeDetail>; onOpenEp: (n: number | null) => void }> = ({ run, n, load, onOpenEp }) => {
  const [detail, setDetail] = useState<SkillEpisodeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let on = true; setDetail(null); setError(null); setCopied(false);
    load(n).then((d) => { if (on) setDetail(d); }).catch(() => { if (on) setError('Chưa đọc được bản sạch của tập này. Hãy thử mở lại.'); });
    return () => { on = false; };
  }, [n, load]);
  const ep = run.writing?.episodes.find((e) => e.n === n);
  const nav = readerNavOf(run.writing, n);
  const chips = readerChipsOf(ep, detail, run.input.limits.maxEpisodeSeconds);
  const copy = async (): Promise<void> => {
    if (!detail) return;
    try { await navigator.clipboard.writeText(detail.scriptText); setCopied(true); } catch { setError('Chưa sao chép được; hãy bôi đen và sao chép thủ công.'); }
  };
  return (
    <div className="panel soft" id="reader" data-testid="reader">
      <div className="row between dochead">
        <div><span className="eyebrow">Đọc ngay · không cần chờ viết xong</span><h3>Tập {n}{ep?.title ? ` · ${ep.title}` : ''}</h3></div>
        <div className="row">
          {chips.map((c) => <span key={c.text} className={`chip ${c.tone}`}>{c.text}</span>)}
          <button type="button" className="btn" data-testid="reader-prev" disabled={nav.prev === null} onClick={() => nav.prev !== null && onOpenEp(nav.prev)}>‹ Tập trước</button>
          <button type="button" className="btn" data-testid="reader-next" disabled={nav.next === null} onClick={() => nav.next !== null && onOpenEp(nav.next)}>Tập sau ›</button>
          <button type="button" className="btn" data-testid="reader-copy" disabled={!detail} onClick={() => void copy()}>{copied ? 'Đã sao chép' : 'Sao chép kịch bản'}</button>
          <button type="button" className="btn" data-testid="reader-close" onClick={() => onOpenEp(null)}>Đóng</button>
        </div>
      </div>
      {error ? <Alert kind="bad">{error}</Alert> : null}
      {!detail && !error ? <p className="muted" data-testid="reader-loading">Đang mở bản sạch…</p> : null}
      {detail ? (
        <>
          {detail.overlongNote ? <Alert>{detail.overlongNote}</Alert> : null}
          <div className="sp" data-testid="reader-script">{scriptBlocksOf(detail.scriptText).map((b, i) => (b.kind === 'blank' ? <div key={i} className="blank" /> : <div key={i} className={b.kind === 'title' ? 't' : b.kind === 'scene' ? 'sc' : b.kind === 'name' ? 'nm' : 'ln'}>{b.text}</div>))}</div>
          <p className="note" style={{ textAlign: 'center' }}>{READER_NOTE}</p>
        </>
      ) : null}
    </div>
  );
};

export const SkillWritingScreen: React.FC<WritingScreenProps> = ({ run, stateId, busy, openEp, loadEpisode, onOpenEp, onPause, onResume, onRaiseCap, onBoundary, onStopEarly }) => {
  const copy = writingCopyOf(run, stateId);
  const cells = episodeCellsOf(run.writing, openEp);
  const cards = copy.baseState === 'writing-boundary' ? boundaryCardsOf(run) : [];
  const onPrimary = copy.primary.kind === 'raise-cap' ? onRaiseCap : copy.primary.kind === 'resume' ? onResume : onPause;
  return (
    <>
      <div className="panel" data-testid={`writing-panel-${copy.baseState}`}>
        <div className="row between">
          <div><span className="eyebrow">{copy.eyebrow}</span><h2 data-testid="writing-headline">{copy.headline}</h2><p className="muted">{copy.body}</p></div>
          <span className={`chip ${copy.chipTone}`} data-testid="writing-chip">{copy.chip}</span>
        </div>
        <div className="progress"><span style={{ width: `${copy.progressPct}%` }} /></div>
        <div className="episodes" data-testid="writing-episodes">
          {cells.map((c) => (
            <button key={c.n} type="button" className={`ep ${c.tone} ${c.selected ? 'sel' : ''}`} data-testid={`ep-${c.n}`} disabled={!c.clickable} aria-label={c.aria} aria-pressed={c.selected}
              onClick={() => onOpenEp(c.selected ? null : c.n)}>{c.mark}{c.label}</button>
          ))}
        </div>
        {copy.alert ? <Alert kind={copy.alert.kind === 'warn' ? '' : copy.alert.kind}>{copy.alert.text}{copy.moneyLine ? <><br />{copy.moneyLine}</> : null}</Alert> : null}
        {cards.map((b) => (
          <div className="decision" key={b.n} data-testid={`boundary-${b.n}`}>
            <div className="row between"><b>{b.title}</b><span className="chip warn">Chỉ tập {b.n} dừng</span></div>
            <p style={{ margin: '8px 0 4px' }}>{b.reason}{b.estimateLine ? ` ${b.estimateLine}` : ''}</p>
            <div className="rec"><span className="tag keep">Máy khuyến nghị</span> {b.recommendation} <span className="muted">({b.affectedLabel})</span></div>
            <div className="row" style={{ marginTop: 12 }}>
              <button type="button" className="btn primary" data-testid={`boundary-recommend-${b.n}`} disabled={busy || b.recommendDisabled} onClick={() => onBoundary(b.n, 'RECOMMENDED')}>{b.recommendLabel}</button>
              <button type="button" className="btn" data-testid={`boundary-keep-${b.n}`} disabled={busy} onClick={() => onBoundary(b.n, 'KEEP')}>{b.keepLabel}</button>
              {b.disabledReason ? <span className="note" data-testid={`boundary-reason-${b.n}`} style={{ margin: 0 }}>{b.disabledReason}</span> : <span className="note" style={{ margin: 0 }}>Bạn không cần soạn cách sửa.</span>}
            </div>
          </div>
        ))}
        {copy.statusLine ? <div className="statusline" data-testid="writing-statusline"><span>{copy.statusLine}</span><span>Chưa cần bạn · chỉ hiện “Cần bạn” khi phải đổi quyết định đã chốt</span></div> : null}
      </div>
      {openEp ? <EpisodeReader run={run} n={openEp} load={loadEpisode} onOpenEp={onOpenEp} /> : null}
      <footer className="row between action">
        <div className="grow"><b>{copy.footerTitle}</b><p className="muted">{copy.footerSub}</p></div>
        {copy.secondary ? <button type="button" className="btn" data-testid="writing-secondary" disabled={busy} onClick={onStopEarly}>{copy.secondary.label}</button> : null}
        {copy.primary.kind !== 'none' || copy.primary.label
          ? <button type="button" className={copy.primary.kind === 'pause' ? 'btn' : 'btn primary'} data-testid="writing-primary" disabled={busy || copy.primary.disabled} onClick={onPrimary}>{copy.primary.label}</button> : null}
      </footer>
    </>
  );
};
